import { Component, computed, inject, OnInit, ViewChild } from '@angular/core';
import { firstValueFrom, Observable, skipWhile, take } from 'rxjs';
import { Room } from '../../../core/models/entities/room';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { ActivatedRoute, Router } from '@angular/router';
import { bookingsActions } from 'angular/app/core/store/bookings';
import { roomsActions } from 'angular/app/core/store/rooms';
import { PublicBookingComponent } from '../../../shared/components/public-booking/public-booking.component';
import { Title } from '@angular/platform-browser';
import { VendorsService } from '../../../core/services/vendors.service';
import { Vendor } from '../../../core/models/entities/vendor';
import { ToastService } from '../../../core/services/toast.service';
import { Booking } from '../../../core/models/entities/booking';
import {
  selectAuthUser,
  selectRoomsError,
  selectRoomsLoading,
} from '../../../core/store/reducers';
import { BookableBy } from '../../../core/models/common/bookable-by';
import { BookingPermission } from '../../../core/models/entities/permission';
import { AuthService } from '../../../core/services/auth.service';
import { LogoutBaseComponent } from '../../../shared/components/logout-base/logout-base.component';
import { ModalService } from '../../../core/services/modal.service';
import { RoomsService } from '../../../core/services/rooms.service';

@Component({
  selector: 'app-booking-page',
  template: `
    @if (
      (room$ | async) &&
      !roomLoadingError() &&
      !roomIsInactiveOrPrivateOrNotListed() &&
      !authUserIsNotAllowedToCreatePublicBooking()
      ) {
      @if (room$ | async; as room) {
        <div class="container pb-12 pt-6">
          <app-breadcrumb
            class="hidden md:block"
          [links]="[
            {
              title: 'Rechercher une salle',
              routerLink: '/louer-salle/' + room.vendor.slug,
            },
            {
              title: room.name,
              routerLink:
                '/louer-salle/' + room.vendor.slug + '/salle/' + room.id,
            },
            {
              title: 'Réserver',
            },
          ]"
          ></app-breadcrumb>
          <div class="font-medium uppercase md:hidden"
            >Réserver : {{ room.name }}
          </div>
          <app-public-booking
            #publicBookingComponent
            (bookingSaved)="bookingSaved($event)"
          ></app-public-booking>
        </div>
      }
    } @else if (roomLoading()) {
      <app-loader [centered]="true"></app-loader>
    } @else if (roomLoadingError() || roomIsInactiveOrPrivateOrNotListed()) {
      <app-not-found></app-not-found>
    } @else if (authUserIsNotAllowedToCreatePublicBooking()) {
      <div class="container pt-10 text-center md:pt-40">
        <p class="text-3xl font-bold uppercase">Déconnexion requise</p>
        <p class="mt-4 text-lg"
          >Vous êtes connecté·e à un compte utilisateur ({{
          authUser()?.email
          }}) qui ne vous permet pas d'enregistrer une réservation publique.<br />
          Pour effectuer une réservation en tant que client, veuillez vous
          <a
            class="underline underline-offset-4"
            (click)="logout(redirectUrl())"
            >déconnecter de votre compte</a
            >.</p
            >
          </div>
        }
    `,
  standalone: false,
})
export class BookingPageComponent
  extends LogoutBaseComponent
  implements OnInit
{
  @ViewChild('publicBookingComponent')
  publicBookingComponent: PublicBookingComponent;
  roomsService = inject(RoomsService);
  room$: Observable<Room> = this.store.select(fromStore.selectBookingRoom);
  roomLoadingError = this.store.selectSignal(selectRoomsError);
  roomLoading = this.store.selectSignal(selectRoomsLoading);
  roomIsInactiveOrPrivateOrNotListed = computed(() => {
    const room = this.store.selectSignal(fromStore.selectBookingRoom);
    const authUser = this.store.selectSignal(selectAuthUser);
    const isInternalUser = authUser()?.vendors?.some(
      (v) => v.id === room()?.vendor_id,
    );

    return (
      room()?.bookable_by === BookableBy.Private ||
      !room()?.active ||
      !this.roomsService.canUserAccessRoom(room()) ||
      (!isInternalUser && !room()?.is_bookable_publicly)
    );
  });
  authUser = this.store.selectSignal(selectAuthUser);
  isLoggedIn = this.store.selectSignal(fromStore.isLoggedIn);
  authUserIsNotAllowedToCreatePublicBooking = computed(() => {
    if (!this.isLoggedIn()) {
      return false;
    }

    return !this.authUser()?.all_permissions.includes(
      BookingPermission.StorePublic,
    );
  });
  // Hack fait rapidement pour fix prod -> le logout sans redirection affichait unauthorizedError() (room pas définie)
  redirectUrl = computed(() => {
    const vendor = this.store.selectSignal(fromStore.selectPublicVendor);
    if (!vendor()) {
      return '';
    }

    return `hote/${vendor().slug}`;
  });

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    public router: Router,
    private title: Title,
    private vendorsService: VendorsService,
    authService: AuthService,
    toastService: ToastService,
    modalService: ModalService,
  ) {
    super(modalService, authService, toastService);
  }

  ngOnInit() {
    if (this.route.snapshot.queryParams['payment_failed']) {
      this.toastService.error(
        'Le paiement a échoué. Veuillez réessayer.',
        null,
        null,
      );
    }
    const id = parseInt(this.route.snapshot.params['id'], 10);
    this.store.dispatch(roomsActions.findOne({ id }));
    this.store
      .select(fromStore.selectBookingDraft)
      .pipe(take(1))
      .subscribe((booking) => {
        if (booking.id) {
          this.store.dispatch(bookingsActions.resetBooking());
        }
        this.store.dispatch(
          bookingsActions.setBookingData({
            room_id: id,
          }),
        );
      });
    this.setPageTitle();
  }

  setPageTitle() {
    this.room$
      .pipe(
        skipWhile((value) => value === undefined),
        take(1),
      )
      .subscribe((room: Room) => {
        firstValueFrom(
          this.vendorsService.findOne(this.route.snapshot.params['vendorSlug']),
        ).then((vendor: Vendor) => {
          this.title.setTitle(
            'Location de salles - Réserver ' + room.name + ', ' + vendor.name,
          );
        });
      });
  }

  async bookingSaved(booking: Booking) {
    const user = await firstValueFrom(
      this.store.select(fromStore.selectAuthUser).pipe(take(1)),
    );
    const room = await firstValueFrom(this.room$.pipe(take(1)));
    if (user && user.vendor?.id === room.vendor_id) {
      this.store.dispatch(bookingsActions.resetBooking());
      this.toastService.success(
        'Réservation enregistrée pour ' + booking.full_name,
      );
      this.router.navigateByUrl('/reservations?date=' + booking.start);
    } else {
      this.router.navigate([
        '/louer-salle',
        this.route.snapshot.params['vendorSlug'],
        'salle',
        this.route.snapshot.params['id'],
        'confirmation',
      ]);
    }
  }
}
